import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { ApiHandler } from "../../helper/ApiHandler";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loading from "../../components/Loading";

const AddDomain = () => {
    const { userToken } = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [domain, setDomain] = useState("");
    const [userEmail, setUserEmail] = useState(null);
    const [userName, setUserName] = useState("");
    const [appName, setAppName] = useState("");
    const [options, setOptions] = useState([]);
    const [searchText, setSearchText] = useState(""); // Stores user input
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const debounceRef = useRef(null);

    // Function to fetch users
    const fetchUsers = async (query) => {
        if (query.length < 4) return;

        setLoading(true);
        try {
            const response = await ApiHandler(
                "/get_userList_for_domain.php",
                "POST",
                { email: query },
                userToken,
                dispatch,
                navigate
            );

            if (response.data.status === "1" && response.data.data.length > 0) {
                const users = response.data.data.map((user) => ({
                    value: user.email,
                    label: user.email,
                    userName: user.full_name,
                    id: user.id, // <--- this is the dynamic ID
                    app_name: user.app_name
                }));
                setOptions(users);
            } else {
                setOptions([{ value: "", label: "No user email found", isDisabled: true }]);
            }
        } catch (error) {
            console.error("Error fetching users", error);
            setOptions([{ value: "", label: "Error fetching users", isDisabled: true }]);
        } finally {
            setLoading(false);
        }
    };

    // Handles user search input with debounce
    const handleSearch = (inputValue) => {
        setSearchText(inputValue);

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            fetchUsers(inputValue);
        }, inputValue.length > 4 ? 2000 : 1000); // 1 second for first input, 2-3 seconds for additional input
    };

    // Handle user selection
    const handleSelect = (selectedOption) => {
        if (selectedOption) {
            setUserEmail(selectedOption);
            setUserName(selectedOption.userName);
            setSearchText(selectedOption.label);
            setAppName(selectedOption.app_name || "");
        } else {
            setUserEmail(null);
            setUserName("");
            setAppName("");
            setSearchText("");
            setOptions([]);
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!domain || !userEmail || !userEmail.value || !appName) {
            setError("All fields are required");
            return;
        }

        setError(null);

        try {
            setLoading(true);
            const response = await ApiHandler(
                "/add_domain.php",
                "POST",
                {
                    user_id: userEmail.id, // Use the dynamic user ID from the selected user
                    user_name: userName,
                    user_email: userEmail.value,
                    domain_name: domain,
                    app_name: appName,
                },
                userToken,
                dispatch,
                navigate
            );

            if (response.data.status === "1") {
                toast.success(response.data.msg || "Domain successfully added!");
                setDomain("");
                setUserEmail(null);
                setUserName("");
                setAppName("");
                setSearchText("");
                navigate("/admin/users/domains");
            } else {
                toast.error(response.data.msg);
            }
        } catch (error) {
            console.error("Error submitting domain", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto bg-white p-6 shadow-lg rounded-lg mt-10 border border-orange-100">
            <h2 className="text-3xl font-bold mb-6 text-center text-gray-700">Add Domain</h2>
            {error && <p className="text-red-500 text-center mb-4">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block font-medium text-gray-700">App Url</label>
                    <input
                        type="text"
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        className="w-full p-3 border rounded-lg mt-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Enter App Url"
                        required
                    />
                </div>
                <div>
                    <label className="block font-medium text-gray-700">User Email</label>
                    <Select
                        options={options}
                        onInputChange={handleSearch}
                        onChange={handleSelect}
                        isLoading={loading}
                        inputValue={searchText}
                        onMenuClose={() => setSearchText(searchText)} // Preserve input on blur
                        placeholder="Search user email"
                        className="mt-1"
                        isClearable
                        required
                    />
                </div>
                <div>
                    <label className="block font-medium text-gray-700">User Name</label>
                    <input
                        type="text"
                        value={userName}
                        className="w-full p-3 border rounded-lg mt-1 bg-gray-200 focus:outline-none"
                        disabled
                    />
                </div>
                <div>
                    <label className="block font-medium text-gray-700">App Name</label>
                    <input
                        type="text"
                        value={appName}
                        onChange={(e) => setAppName(e.target.value)}
                        className="w-full p-3 border rounded-lg mt-1 focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Enter or edit App Name"
                        required
                    />
                </div>
                <button
                    type="submit"
                    className="w-full bg-orange-600 text-white p-3 rounded-lg mt-4 hover:bg-orange-700 transition duration-300"
                >
                    Submit
                </button>
            </form>
        </div>
    );
};

export default AddDomain;
